import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AppIcon,
  ConfirmDelete,
  ContentNugget,
  PopoverFilterMenu,
  addHttpIfMissing,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import {
  StickerCardId,
  useToolCardById,
} from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { useGetLinkPreviewQuery } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import {
  PRIORITY_TASK_STATUS_CODES,
  PriorityTask,
  PriorityTaskPost,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, ButtonBase, Theme, SxProps, Typography, ClickAwayListener } from '@mui/material';

import { TaskCardNuggetContent } from '../atoms/TaskCardNuggetContent';
import { TASK_CARD_MIN_WIDTH } from '../constants/priorities.constants';
import { getToggledTaskStatusCode, transformTaskContentsToNuggets } from '../utils/tasks.utils';

const TASK_CARD_STRINGS = appStrings.modules.priorities.taskCard;
const TASK_PAGE_STRINGS = appStrings.modules.priorities.taskPage;

export type TaskCardProps = Readonly<{
  task: PriorityTask;

  /** Hides the status toggle and the actions menu; linked content stays clickable. */
  readOnly?: boolean;

  isInSidebar?: boolean;
  isHighlighted?: boolean;
  isDraggable?: boolean;

  topRightElement?:
    | {
        type: 'ELLIPSIS_MENU';
      }
    | { type: 'ELEMENT'; element: ReactNode };

  restrictWidth?: boolean;
  opacity?: number;
  deleteTask?: (taskId: number) => void;
  openEditTaskDialog?: (task: PriorityTask) => void;
  onCardClick?: () => void;
  onOpen?: (task: PriorityTask) => void;
  updateTask?: (task: Partial<PriorityTaskPost>) => Promise<void>;
  handleDuplicateTask?: (task: PriorityTask) => void;
}>;

export const TaskCard = ({
  task,
  readOnly = false,
  isInSidebar = false,
  isHighlighted = false,
  isDraggable = false,
  topRightElement = { type: 'ELLIPSIS_MENU' },
  restrictWidth = true,
  opacity = 1,
  deleteTask,
  openEditTaskDialog,
  onCardClick,
  onOpen,
  updateTask,
  handleDuplicateTask,
}: TaskCardProps) => {
  const navigate = useNavigate();
  const { getStickerCard } = useToolCardById({ navigate });

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { data: linkPreview } = useGetLinkPreviewQuery(task.content_url || '', {
    skip: !task.content_url,
  });

  const completed = task.status_code === PRIORITY_TASK_STATUS_CODES.COMPLETED;
  const contentTransformedToNuggets = transformTaskContentsToNuggets(task);
  const hasContent = contentTransformedToNuggets.length > 0 || !!task.content_url;

  const handleNuggetClick = (nugget: ContentNugget) => {
    if (nugget.variant === 'tool' && nugget.tool_info) {
      const stickerCard = getStickerCard(nugget.tool_info.tool_id as StickerCardId);

      if (stickerCard && stickerCard.onClick) {
        stickerCard.onClick();

        return;
      }
    }

    if (task.content_url) {
      window.open(addHttpIfMissing(task.content_url), '_blank');

      return;
    }

    handlePanelNuggetClick(nugget, navigate);
  };

  const externalLinkAsNugget: ContentNugget = {
    id: 0,
    slug: '',
    title: linkPreview?.title || task.content_url || '',
    description: linkPreview?.description || '',
    external_url: task.content_url || '',
    variant: 'resource',
    is_browsable: true,
    type: 'content',
    star_rating: 0,
    value_rating: 0,
    preview_media: {
      id: 0,
      media_url: linkPreview?.images?.[0] || '',
    },
  };

  return (
    <Box
      id={`task-card-${task.id}`}
      sx={containerSx(
        restrictWidth,
        !!onCardClick || !!onOpen,
        readOnly,
        isHighlighted,
        isInSidebar,
        isDraggable,
      )}
      onClick={(event) => {
        onCardClick?.();

        // Clicks inside the menu and delete dialog bubble up through their portals.
        if (onOpen && event.currentTarget.contains(event.target as Node)) {
          onOpen(task);
        }
      }}
    >
      <ConfirmDelete
        openDialog={confirmDeleteOpen}
        setOpenDialog={setConfirmDeleteOpen}
        headerText={TASK_CARD_STRINGS.deleteTask}
        bodyText={TASK_CARD_STRINGS.deleteTaskConfirmBody}
        handleDelete={() => deleteTask?.(task.id)}
      />
      {!readOnly && (
        <PopoverFilterMenu
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          categories={[
            {
              label: 'Actions',
              items: [
                {
                  label: TASK_CARD_STRINGS.editTask,
                  value: 'edit',
                  category: 'action',
                  icon: 'Edit05',
                  onClick: () => openEditTaskDialog?.(task),
                  closeOnClick: true,
                },
                {
                  label: TASK_CARD_STRINGS.duplicateTask,
                  value: 'duplicate',
                  category: 'action',
                  icon: 'Copy04',
                  onClick: () => handleDuplicateTask?.(task),
                  closeOnClick: true,
                },
                {
                  label: TASK_CARD_STRINGS.deleteTask,
                  value: 'delete',
                  category: 'action',
                  icon: 'Trash01',
                  onClick: () => setConfirmDeleteOpen(true),
                  closeOnClick: true,
                },
              ],
            },
          ]}
          singleLevel
        />
      )}

      <Box sx={headerSx(opacity)}>
        <Box sx={titleAndDateSx}>
          <Typography variant="text-lg-semibold" sx={titleSx}>
            {task.title}
          </Typography>

          {task.due_date && (
            <Typography variant="text-sm-regular" sx={dueDateSx}>
              {TASK_PAGE_STRINGS.due}{' '}
              {new Date(task.due_date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </Typography>
          )}
        </Box>

        {!readOnly && (
          <ButtonBase
            id={`task-card-status-toggle-${task.id}`}
            aria-label={TASK_PAGE_STRINGS.toggleCompleteAria}
            aria-pressed={completed}
            sx={iconButtonSx}
            onClick={(event) => {
              event.stopPropagation();
              updateTask?.({ status_code: getToggledTaskStatusCode(task.status_code) });
            }}
          >
            <AppIcon
              iconName={completed ? 'CheckCircleBroken' : 'ClockFastForward'}
              diameter="20px"
              color={
                completed
                  ? 'Colors/Foreground/fg-success-primary'
                  : 'Colors/Foreground/fg-brand-primary'
              }
            />
          </ButtonBase>
        )}

        {topRightElement.type === 'ELLIPSIS_MENU' && !readOnly && (
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <ButtonBase
              id={`task-card-menu-button-${task.id}`}
              sx={iconButtonSx}
              onClick={(event) => {
                event.stopPropagation();
                setAnchorEl(event.currentTarget);
              }}
            >
              <AppIcon
                iconName="DotsVertical"
                diameter="20px"
                color="Colors/Foreground/fg-quinary"
              />
            </ButtonBase>
          </ClickAwayListener>
        )}

        {topRightElement.type === 'ELEMENT' && topRightElement.element}
      </Box>

      {hasContent && (
        <Box sx={contentSectionSx(opacity)}>
          {contentTransformedToNuggets.map((nugget) => (
            <TaskCardNuggetContent
              key={nugget.id}
              id={`task-card-content-${task.id}-${nugget.id}`}
              nugget={nugget}
              onClick={() => handleNuggetClick(nugget)}
            />
          ))}

          {task.content_url && (
            <TaskCardNuggetContent
              id={`task-card-link-${task.id}`}
              nugget={externalLinkAsNugget}
              onClick={() => handleNuggetClick(externalLinkAsNugget)}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const containerSx =
  (
    restrictWidth: boolean,
    isClickable?: boolean,
    isDisabled?: boolean,
    isHighlighted?: boolean,
    isInSidebar?: boolean,
    isDraggable?: boolean,
  ): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',

    width: '100%',
    maxWidth: restrictWidth ? '100%' : `${TASK_CARD_MIN_WIDTH}px`,
    borderRadius: backrs.radius['radius-xl'],
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],

    cursor: isDraggable ? 'grab' : isClickable ? 'pointer' : 'default',

    // highlight state
    ...(isHighlighted && {
      border: `1px solid ${backrs.colors['Brand/300']}`,
    }),

    // basic hover state for the sidebar
    ...(isInSidebar && {
      '&:hover': {
        border: `1px solid ${backrs.colors['Brand/300']}`,
      },
    }),

    // outside of the sidebar, we use the standard hover state for cards
    ...(isClickable &&
      !isInSidebar &&
      !isDisabled && {
        transition: 'all 0.3s ease-in-out',

        cursor: 'pointer',
        '&:hover': {
          borderColor: backrs.colors['Colors/Foreground/fg-primary'],
          transform: 'translate(-2px, -2px)',
          boxShadow: backrs.shadows['shadow-lg'],
        },

        '&:active': {
          transform: 'none !important',
          boxShadow: 'none !important',
          transition: 'all 0s !important',
        },
      }),
  });

const headerSx =
  (opacity: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: backrs.spacing['spacing-lg'],
    paddingX: backrs.spacing['spacing-3xl'],
    paddingY: backrs.spacing['spacing-2xl'],
    opacity,
  });

const titleAndDateSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  flex: 1,
  minWidth: 0,
});

const titleSx: SxProps<Theme> = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
};

const dueDateSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const iconButtonSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  borderRadius: backrs.radius['radius-full'],
});

const contentSectionSx =
  (opacity: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    opacity,
  });
